import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Eye } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import gameService from '../../services/gameService';
import { getFullImageUrl } from '../../utils/imageUpload';
import GameModal from '../../components/Modals/GameModal';
import toast from 'react-hot-toast';

const GamesContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const GamesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 30px;
`;

const GameCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  display: flex;
  min-height: 120px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%);
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }
`;

const GameImage = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: white;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const GameContent = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const GameHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const GameInfo = styled.div`
  flex: 1;
`;

const GameTitle = styled.h3`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 5px 0;
  line-height: 1.4;
`;

const GameId = styled.div`
  color: #6c6c8a;
  font-size: 12px;
  margin-bottom: 10px;
`;

const GamePlatform = styled.div`
  color: #b8b8d1;
  font-size: 14px;
  margin-bottom: 10px;
`;

const GameActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #2d2d44;
  border-radius: 6px;
  padding: 8px;
  color: #b8b8d1;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2d2d44;
    color: #00d4ff;
    border-color: #00d4ff;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const Games = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showGameModal, setShowGameModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await gameService.getGames();
      setGames(response || []);
    } catch (error) {
      toast.error('خطا در بارگذاری بازی‌ها');
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGames = games.filter(game =>
    game.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    game.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddGame = () => {
    setSelectedGame(null);
    setShowGameModal(true);
  };

  const handleEditGame = (game) => {
    setSelectedGame(game);
    setShowGameModal(true);
  };

  const handleDeleteGame = async (game) => {
    if (window.confirm('آیا از حذف این بازی اطمینان دارید؟')) {
      try {
        await gameService.deleteGame(game.id);
        toast.success('بازی با موفقیت حذف شد');
        fetchGames();
      } catch (error) {
        toast.error('خطا در حذف بازی');
        console.error('Error deleting game:', error);
      }
    }
  };

  const handleGameModalClose = () => {
    setShowGameModal(false);
    setSelectedGame(null);
  };

  const handleGameModalSuccess = () => {
    fetchGames();
  };

  if (loading) {
    return (
      <GamesContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ color: '#b8b8d1' }}>در حال بارگذاری بازی‌ها...</div>
        </div>
      </GamesContainer>
    );
  }

  return (
    <GamesContainer>
      <Header>
        <Title>مدیریت بازی‌ها</Title>
        <SearchContainer>
          <Input
            placeholder="جستجو در بازی‌ها..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ minWidth: '250px' }}
          />
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
          <Button variant="primary" onClick={handleAddGame} icon={<Plus size={16} />}>
            افزودن بازی
          </Button>
        </SearchContainer>
      </Header>

      <GameModal
        show={showGameModal}
        game={selectedGame}
        onClose={handleGameModalClose}
        onSuccess={handleGameModalSuccess}
      />

      {filteredGames.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🎮</EmptyIcon>
          <EmptyTitle>هیچ بازی‌ای یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'نتیجه‌ای برای جستجوی شما یافت نشد' : 'هنوز بازی‌ای اضافه نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <GamesList>
          {filteredGames.map((game, index) => (
            <GameCard
              key={game.id || index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <GameImage>
                {game.picture ? (
                  <img src={getFullImageUrl(game.picture)} alt={game.name} />
                ) : (
                  '🎮'
                )}
              </GameImage>
              
              <GameContent>
                <GameHeader>
                  <GameInfo>
                    <GameTitle>{game.name || 'بازی بدون نام'}</GameTitle>
                    <GameId>ID: #{game.id}</GameId>
                    <GamePlatform>پلتفرم: {game.platform || 'نامشخص'}</GamePlatform>
                  </GameInfo>
                  
                  <GameActions>
                    <ActionButton onClick={() => handleEditGame(game)} title="ویرایش">
                      <Edit size={16} />
                    </ActionButton>
                    <ActionButton onClick={() => handleDeleteGame(game)} title="حذف">
                      <Trash2 size={16} />
                    </ActionButton>
                  </GameActions>
                </GameHeader>
              </GameContent>
            </GameCard>
          ))}
        </GamesList>
      )}
    </GamesContainer>
  );
};

export default Games;
